import { useEffect, useState, useRef, useCallback } from "react";

// Use public path for preload discoverability
const heroHumanoid = "/images/hero-humanoid.webp";
export const HeroVisual = () => {
  const [mounted, setMounted] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const rafRef = useRef<number | null>(null);

  // Use requestAnimationFrame to batch scroll updates and prevent forced reflows
  const handleScroll = useCallback(() => {
    if (rafRef.current !== null) return; // Skip if RAF already scheduled
    
    rafRef.current = requestAnimationFrame(() => {
      setScrollY(window.scrollY);
      rafRef.current = null;
    });
  }, []);

  useEffect(() => {
    setMounted(true);
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleScroll]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Dark gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-background" />
      
      {/* Subtle grid overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(6, 182, 212, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(6, 182, 212, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
        }}
      />

      {/* Humanoid silhouette - positioned lower with parallax */}
      <div className="absolute inset-0 flex items-end justify-center">
        <div 
          className={`relative transition-opacity duration-1000 ease-out ${
            mounted ? 'opacity-60' : 'opacity-0'
          }`}
          style={{ 
            marginBottom: '-10%',
            transform: `translateY(${scrollY * 0.3}px) scale(${1 - scrollY * 0.0002})`,
            willChange: 'transform',
          }}
        >
          {/* Glow effect behind humanoid */}
          <div 
            className="absolute inset-0 blur-3xl"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(6, 182, 212, 0.25) 0%, transparent 70%)',
              transform: 'scale(1.5)',
            }}
          />
          
          {/* Main humanoid image */}
          <img
            src={heroHumanoid}
            alt=""
            width={1920}
            height={1080}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="relative z-10 h-[70vh] md:h-[85vh] lg:h-[95vh] w-auto object-contain max-w-none"
            style={{
              filter: 'drop-shadow(0 0 60px rgba(6, 182, 212, 0.5))',
              maskImage: 'linear-gradient(to top, black 50%, transparent 95%)',
              WebkitMaskImage: 'linear-gradient(to top, black 50%, transparent 95%)',
            }}
          />
        </div>
      </div>

      {/* Gradient overlays for content readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/70 to-transparent" style={{ height: '60%' }} />
      
      {/* Side gradients for focus */}
      <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-slate-950/90 to-transparent" />
      <div className="absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-slate-950/90 to-transparent" />

      {/* Animated particles */}
      <div className="absolute inset-0">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/40 rounded-full"
            style={{
              left: `${10 + (i * 7) % 80}%`,
              top: `${20 + (i * 11) % 60}%`,
              animation: `float ${4 + (i % 3)}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>

      {/* Corner tech accents */}
      <div className="absolute top-0 left-0 w-48 h-48">
        <div className="absolute top-12 left-0 w-32 h-px bg-gradient-to-r from-cyan-500/40 to-transparent" />
        <div className="absolute top-0 left-12 h-32 w-px bg-gradient-to-b from-cyan-500/40 to-transparent" />
      </div>
      <div className="absolute top-0 right-0 w-48 h-48">
        <div className="absolute top-12 right-0 w-32 h-px bg-gradient-to-l from-cyan-500/40 to-transparent" />
        <div className="absolute top-0 right-12 h-32 w-px bg-gradient-to-b from-cyan-500/40 to-transparent" />
      </div>
    </div>
  );
};
